import React, { useState, useEffect } from 'react';
import { ErrorMessage } from '@atlaskit/form';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { isValidTimeString } from '@atlassian/jira-issue-format-time/src/index.tsx';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import { DATA_TEST_IDS } from './utils.tsx';

const TimeEstimate = (props: Props): React.JSX.Element => {
	const {
		value,
		placeholder = '2w 1d 5h 4m',
		onChange,
		config,
		shouldShowWarning,
		fieldId,
		...fieldProps
	} = props;
	const [isIncorrectTimeFormat, setIsIncorrectTimeFormat] = useState(false);
	const [debouncedIsValidTimeString] = useDebouncedCallback(
		(inputValue: string) => setIsIncorrectTimeFormat(!isValidTimeString(config)(inputValue)),
		500,
	);

	useEffect(() => {
		debouncedIsValidTimeString(value);
	}, [value, debouncedIsValidTimeString]);

	const { formatMessage } = useIntl();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onValueChange = (e: any) => {
		const { value: inputValue } = e.target;
		onChange(inputValue);
	};

	return (
		<>
			<Textfield
				{...fieldProps}
				testId={DATA_TEST_IDS.TEXT_FIELD}
				{...(fieldId && {
					'aria-describedby': `${fieldId}-field-message`,
				})}
				type="text"
				value={value}
				placeholder={placeholder}
				onChange={onValueChange}
				isInvalid={isIncorrectTimeFormat}
				aria-label={formatMessage(messages.ariaLabel)}
				elemAfterInput={
					shouldShowWarning === true ? (
						<Tooltip
							content={formatMessage(messages.timeLoggedExceeded)}
							testId={DATA_TEST_IDS.TOOLTIP}
						>
							<WarningIcon
								spacing="spacious"
								label={formatMessage(messages.warning)}
								color={token('color.icon.warning')}
								testId={DATA_TEST_IDS.WARNING_ICON}
							/>
						</Tooltip>
					) : null
				}
			/>
			{isIncorrectTimeFormat && (
				<ErrorMessage testId={DATA_TEST_IDS.ERROR_MESSAGE}>
					{formatMessage(messages.invalidDateFormat)}
				</ErrorMessage>
			)}
		</>
	);
};

export { TimeEstimate };
